import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import AddActivityForm from './AddActivityForm';
import { T } from './activityStepper.config';
import InlineBrandLoader from './InlineBrandLoader';
import {
    getCachedCaseActivities,
    setCachedCaseActivities,
} from './caseActivitiesCache';
import CaseActivityList from './CaseActivityList';

/* Same button recipes as the form — a secondary and a primary must be the same
 * height and type scale, with hierarchy from colour and weight. h-9 matches
 * scale-portal's <Button size="sm"> so the two products agree. */

/**
 * Manage this case's activities: see them, edit one, delete one, add another.
 *
 * ONE DIALOG, TWO MODES
 * ---------------------
 * The list and the form share a single surface and swap places, rather than the
 * form opening on top of the list. A dialog over a dialog was rejected earlier
 * in this feature for the clash warning and the reasoning holds here: two
 * stacked cards with two sets of buttons is harder to read than one card that
 * changes. Going back from the form restores the list with no refetch.
 *
 * WHY THIS IS SEPARATE FROM THE TRACKER
 * ------------------------------------
 * The Case Progress Tracker REPORTS — every row there is something that
 * happened, with a date, that you cannot act on, and activities read the same
 * way. Managing them happens here, behind Case Actions, so there is one place
 * that changes activities and one place that records them.
 */


/** Parsed as parts, never `new Date(str)` — a bare date is read as UTC and can
 *  show the previous day west of Greenwich. */

const CaseActivitiesManager = ({ mediatorId, caseId, caseName, onClose, onChanged }) => {
    const navigate = useNavigate()
    /* OPENS FROM WHAT THE PAGE ALREADY HAS. The Progress Tracker behind this
     * dialog fetched the same list to draw its activity log, so starting empty
     * and showing a loader made the case manager wait for an answer that was
     * already on screen. The fetch below still runs and still wins — this only
     * decides what is on screen while it does. */
    const seeded = getCachedCaseActivities(caseId)
    const [activities, setActivities] = useState(seeded || [])
    const [loading, setLoading] = useState(!seeded)
    const [mode, setMode] = useState(seeded?.length === 0 ? 'form' : 'list')
    const [editing, setEditing] = useState(null)
    const [removing, setRemoving] = useState(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [error, setError] = useState('')
    const openingViewDecided = useRef(Boolean(seeded))

    const load = useCallback(async () => {
        if (!caseId) return
        /* Only BLOCK on the fetch when there is nothing to show. With seeded
           rows this revalidates silently rather than flashing a loader over a
           list the case manager is already reading. */
        if (!getCachedCaseActivities(caseId)) setLoading(true)
        try {
            const res = await api.get('/activities', { params: { mediatorId, caseId } })
            const rows = res?.data?.data || []
            setActivities(rows)
            setCachedCaseActivities(caseId, rows)

            // Choose once on opening; deleting the final row must not open a new form.
            if (!openingViewDecided.current) {
                openingViewDecided.current = true
                if (rows.length === 0) setMode('form')
            }

        } catch (err) {
            console.error('case activities load failed', err)
            setActivities([])
            openingViewDecided.current = true
        } finally {
            setLoading(false)
        }
    }, [caseId, mediatorId])

    useEffect(() => {
        load()
    }, [load])

    const afterChange = () => {
        load()
        /* So the tracker's log and the Schedule board follow the same edit. */
        onChanged?.()
    }

    /* Leaving the form for the list — the one place three controls point at
     * (the cross, "Back to activities" and the footer Close), so they cannot
     * drift apart. */
    const backToList = () => {
        if (activities.length === 0) {
            onClose?.()
            return
        }
        setMode('list')
        setEditing(null)
    }

    /* Open the Schedule page on the month this activity sits in, for THIS
     * case's mediator — a case manager's board shows one mediator at a time, so
     * the date alone would land on whoever happens to be selected. The dialog
     * closes first, or its overlay stays on top of the board. `local_date` is
     * the date the server already resolved; nothing here re-derives it. */
    const openOnSchedule = (activity) => {
        if (!activity?.local_date) return
        onClose?.()
        const params = new URLSearchParams({ date: activity.local_date })
        if (mediatorId) params.set('mediatorId', String(mediatorId))
        /* `activity` as well as `date`: the board opens on the right month AND
           lights up the chip that was clicked. Landing on the month and
           leaving the case manager to find it again is most of why the trip
           felt pointless. The mirror of the board's `?focus=activities`. */
        params.set('activity', String(activity.id))
        navigate(`/schedule?${params.toString()}`)
    }

    const remove = async () => {
        setIsDeleting(true)
        try {
            await api.delete(`/activities/${removing.id}`, { params: { mediatorId } })
            setRemoving(null)
            afterChange()
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                    'The activity could not be deleted.'
            )
        } finally {
            setIsDeleting(false)
        }
    }

    /* ----------------------------------------------------------- form -- */
    if (!openingViewDecided.current && loading) {
        return <InlineBrandLoader label="Please wait…" sublabel="Loading this case's activities" />
    }

    if (mode === 'form') {
        return (
            /* NO heading and NO padded wrapper here: the form owns its title,
               subtitle and footer and renders edge to edge. A heading in this
               shell printed "Add activity" twice, and the wrapper's padding
               stopped the footer reaching the dialog's edges. */
            <div className="relative flex w-full min-w-0 flex-col">
                {/* In FORM mode the cross dismisses the FORM, not the whole
                    dialog — it sits in the corner of a view that was opened
                    from the list behind it, so closing everything loses the
                    list the case manager was working in. Same destination as
                    "Back to activities" and the footer Cancel, so all three
                    agree. */}
                <button
                    type="button"
                    onClick={backToList}
                    aria-label="Back to activities"
                    className="absolute right-1 top-1 z-10 rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                </button>
                <AddActivityForm
                        key={editing?.id || 'new'}
                        mediatorId={mediatorId}
                        activity={editing}
                        lockedCase={{ id: caseId, case_name: caseName }}
                        /* Hidden when the case has no activities: the form was
                           not opened FROM a list, so this would promise a
                           screen that does not exist. */
                        headerLead={
                            activities.length > 0 ? (
                                <button
                                    type="button"
                                    onClick={backToList}
                                    className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-500 transition hover:text-gray-800"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M15 18l-6-6 6-6" />
                                    </svg>
                                    Back to activities
                                </button>
                            ) : null
                        }
                        onCancel={backToList}
                        /* Saving CLOSES the whole dialog rather than falling
                           back to the list. The list is something the mediator
                           asks for from Case Actions; showing it unprompted
                           after a save makes finishing feel like another step.
                           The tracker's log still updates via onChanged. */
                        onSaved={() => {
                            afterChange()
                            onClose?.()
                        }}
                    />
            </div>
        )
    }

    /* ----------------------------------------------------------- list -- */
    return (
        <div className="flex w-full min-w-0 flex-col">
            <div className="relative shrink-0 border-b border-gray-200 pb-2">
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute right-0 top-0 rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                </button>
                <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em', color: T.ink }}>
                    Activities
                </h5>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {caseName}
                </p>
            </div>

            <div style={{ minHeight: 0, maxHeight: 'calc(100dvh - 210px)', overflowY: 'auto', padding: '12px 0' }}>
                {loading ? (
                    <InlineBrandLoader
                        label="Please wait…"
                        sublabel="Loading this case's activities"
                    />
                ) : activities.length === 0 ? (
                    <div className="py-8 text-center">
                        <p className="text-sm text-gray-500">
                            No activities on this case yet.
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                            Record a call or a check-in to see it here and on
                            your calendar.
                        </p>
                    </div>
                ) : (
                    <CaseActivityList activities={activities} caseName={caseName} onOpen={openOnSchedule}
                        onEdit={(activity) => { setEditing(activity); setMode('form') }}
                        onDelete={setRemoving} />
                )}
            </div>

            {/* Tinted and edge to edge, like the form this view swaps with:
                the two are one surface, and a seam between them shows. */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    margin: '0 -24px -24px',
                    padding: '12px 28px',
                    background: T.footer,
                    borderTop: `1px solid ${T.line}`,
                    borderBottomLeftRadius: 12,
                    borderBottomRightRadius: 12,
                }}
            >
                <button
                    type="button"
                    onClick={onClose}
                    style={{
                        height: 44,
                        padding: '0 18px',
                        borderRadius: 7,
                        border: `1px solid ${T.border}`,
                        background: T.white,
                        font: 'inherit',
                        fontSize: 15,
                        fontWeight: 600,
                        color: T.inkSoft,
                        cursor: 'pointer',
                    }}
                >
                    Close
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setEditing(null)
                        setMode('form')
                    }}
                    style={{
                        height: 44,
                        padding: '0 20px',
                        borderRadius: 7,
                        border: 0,
                        background: T.green,
                        font: 'inherit',
                        fontSize: 15,
                        fontWeight: 600,
                        color: T.white,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Add activity
                </button>
            </div>

            {error && (
                <p className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                </p>
            )}

            <ConfirmDialog
                isOpen={!!removing}
                title="Delete Activity"
                cancelText="Cancel"
                confirmText={isDeleting ? 'Deleting...' : 'Delete'}
                tone="danger"
                loading={isDeleting}
                onCancel={() => setRemoving(null)}
                onConfirm={remove}
            >
                Are you sure you want to delete{' '}
                <strong>{removing?.display_title}</strong>? It will be removed
                from the schedule and from the mediator's connected calendar.
                This action cannot be undone.
            </ConfirmDialog>
        </div>
    )
}

export default CaseActivitiesManager
